import { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_TablePagination,
  MRT_TopToolbar,
} from "material-react-table";
import { Box, IconButton, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const WithoutPagination = ({
  data,
  columns,
  pageSize = 5,
  loading,
  enableRowSelection,
  onSelectionChange,
  ...others
}) => {
  const [showFilter, setShowFilter] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");

  const column = useMemo(() => columns.map((el) => ({ ...el })), [columns]);

  const table = useMaterialReactTable({
    columns: column,
    data,
    state: {
      showSkeletons: loading,
      showProgressBars: loading,
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    enableColumnResizing: true,
    enableColumnActions: true,
    enableRowActions: true,
    positionActionsColumn: "last",
    enableGlobalFilter: others.enableCustomGlobalFilter ? false : true,
    enableDensityToggle: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableRowSelection: enableRowSelection || false,
    enableFullScreenToggle: false,
    enableStickyHeader: true,
    layoutMode: "grid",
    displayColumnDefOptions: { "mrt-row-actions": { size: 90 } },

    initialState: {
      density: "comfortable",
      columnPinning: { left: ["mrt-row-actions"], right: [] },
    },

    muiPaginationProps: {
      sx: {
        "& .MuiToolbar-root": {
          justifyContent: "flex-start",
          paddingLeft: 0,
        },
      },
    },

    muiTopToolbarProps: {
      sx: {
        justifyContent: "flex-start",
        gap: 2,
      },
    },
    muiBottomToolbarProps: {
      sx: {
        justifyContent: "flex-start",
        gap: 2,
      },
    },

    muiTableHeadRowProps: {
      sx: {
        background: (theme) =>
          theme.palette.mode === "light"
            ? theme.palette.grey[200]
            : theme.palette.grey[700],
      },
    },
    muiTableContainerProps: {
      sx: { 
        maxHeight: "none",
        overflow: "unset", 
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: (theme) =>
          theme.palette.mode === "light"
            ? theme.palette.grey[600]
            : theme.palette.grey[200],
        paddingLeft: "4px",
        paddingRight: "8px",
        boxShadow: "none",
        background: (theme) =>
          `${
            theme.palette.mode === "light"
              ? theme.palette.grey[200]
              : theme.palette.grey[700]
          }  !important`,
        fontWeight: 600,
        "& .Mui-TableHeadCell-Content": {
          justifyContent: "space-between",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        paddingLeft: "4px",
        paddingRight: "8px",
        boxShadow: "none",
      },
    },
    muiTableHeadCellColumnActionsButtonProps: {
      sx: {
        color: (theme) => theme.palette.primary.contrastText,
        opacity: 1,
      },
    },
    muiTableBodyRowProps: {
      sx: {
        "&.MuiTableRow-root.Mui-selected .MuiTableCell-body": {
          background: (theme) => theme.palette.primary.lighter,
          color: "black",
        },
      },
    },

    renderTopToolbar: ({ table }) => (
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <MRT_TopToolbar table={table} />
        {others.enableCustomGlobalFilter && (
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => setShowFilter(!showFilter)}>
              <SearchIcon />
            </IconButton>
            {showFilter && (
              <TextField
                size="small"
                placeholder="Search..."
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            )}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            p: 1,
          }}
        >
          <MRT_TablePagination table={table} />
        </Box>
      </Box>
    ),
    
    renderBottomToolbar: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          p: 1,
        }}
      >
        <MRT_TablePagination table={table} />
      </Box>
    ),

    ...others,
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [data, table]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(table.getSelectedRowModel().flatRows);
    }
  }, [onSelectionChange, table.getSelectedRowModel().flatRows]);

  return <MaterialReactTable table={table} />;
};

export default WithoutPagination;
